import { useState, useEffect, useRef, useReducer } from "react";
import SpinnerComponent from "../SpinnerComponent/SpinnerComponent";
import QuestionComponent from "../QuestionComponent/QuestionComponent";
import QualificationComponent from "../Qualification/Qualification";
import RankingComponent from "../RankingComponent/RankingComponent";
import styles from "./GameInProgress.module.css";

import { GameProvider } from "../GameContext";

import { ANIMATIONS, GameStates } from "../../utils/constants";
import { useInGameMessages } from "../../hooks/useInGameMessages";
import Modal from "../modal/modal";
import QuestionTextContainer from "../QuestionComponent/QuestionTextContainer/QuestionTextContainer";
import LoadingDoorComponent from "../LoadingDoor/LoadingDoorComponent";

const CURTAIN_ANIMATION_DELAY = 1500;

const CURTAIN_DELAY_BY_STATE = {
  [GameStates.WELCOME]: 0,
  [GameStates.STANDBY]: 0,
  [GameStates.SPINNER]: 500,
  [GameStates.JOKER]: 500,
  [GameStates.IN_ROUND]: 500,
  [GameStates.QUALIFICATION]: 2000,
  [GameStates.FINAL_RANKING]: 2000,
  [GameStates.RANKING]: CURTAIN_ANIMATION_DELAY,
}

function gameCacheReducer(state, action) {
  switch (action.type) {
    case "SET_GAME_CACHE_STATE":
      return {
        ...state,
        ...action.payload,
      };
    // This is used when the game state is the same, but we need to update the game cache state with non critical props.
    case "SET_GAME_CACHE_STATE_NON_CRITICAL_PROPS":
      return {
        ...state,
        ...action.payload,
        state: state.state,
      }
    case "GET_GAME_CACHE_STATE":
      return state;
    default:
      return state;
  }
}

const GameInProgress = ({ user, game, socket, room }) => {
  const [animation, setAnimation] = useState(1);
  const audioRef = useRef(null); // Referencia para el audio
  const [curtainsOpen, setCurtainsOpen] = useState(false);

  const [gameCacheState, setGameCacheState] = useReducer(gameCacheReducer, {});
  const timeOutRef = useRef(null);
  const spinnerTimers = useRef([]);
  const lastChallengeIdRef = useRef(null);
  const { inGameMessage, changeInGameMessage, clearInGameMessage } = useInGameMessages();

  useEffect(() => {

    if (!audioRef.current) {
      return;
    }

    audioRef.current.loop = true;
    audioRef.current.volume = 0.3;

    audioRef.current.addEventListener('loadedmetadata', () => {
      audioRef.current.muted = false;
    });

  }, [game.state]);

  useEffect(() => {
    handleGameUptades(game);
  }, [game]);

  const handleNextMessage = (game) => {
    // If the game state is spinner, we need to check if the challenge id is the same as the last one in order to show the next challenge message.
    if (game?.state === GameStates.SPINNER) {
      if ((lastChallengeIdRef.current === game?.current_question?.cardId && !game?.applyJoker) || (!game?.current_question && !game?.applyJoker)) {
        return;
      };
      lastChallengeIdRef.current = game?.current_question?.cardId;
      changeInGameMessage(game, game?.timeToStart * 1000);
      return;
    }

    changeInGameMessage(game, game?.timeToStart * 1000);
  }

  const handleGameUptades = (game) => {
    if (!game?.state) {
      return;
    }

    if (game?.state !== gameCacheState.state) {
      // Always close the curtains when the game state is welcome or is joker
      if (game?.state === GameStates.WELCOME ||
        (game?.applyJoker && game?.state === GameStates.JOKER)) {
        handleNextMessage(game);
        setCurtainsOpen(false);

        if (game?.applyJoker && game?.state === GameStates.JOKER) {
          timeOutRef.current = setTimeout(() => {
            // clean the messages to avoid box messages overlaping
            clearInGameMessage();
            setGameCacheState({ type: "SET_GAME_CACHE_STATE", payload: { ...game } });
            timeOutRef.current = null;
          }, CURTAIN_DELAY_BY_STATE[GameStates.JOKER]);
        } else {
          setGameCacheState({ type: "SET_GAME_CACHE_STATE", payload: { ...game } });
        }
        return;
      }

      // The spinner state is the state where the game picks a new challenge
      if (game?.state === GameStates.SPINNER) {
        // Always clear all timeout to prevent the curtains from opening at the wrong time
        clearAllSpinnerTimeOuts();
        setCurtainsOpen(false);

        if (!game?.applyJoker) {
          // Game data is cached some milliseconds before to open the curtains
          setTimeout(() => {
            handleNextMessage(game);
            setGameCacheState({ type: "SET_GAME_CACHE_STATE", payload: { ...game } });
          }, CURTAIN_ANIMATION_DELAY);

          spinnerTimers.current.push(setTimeout(() => {
            setCurtainsOpen(true);
            timeOutRef.current = null;
          }, (game?.timeToStart * 1000) + CURTAIN_ANIMATION_DELAY));
          timeOutRef.current = spinnerTimers.current[spinnerTimers.current.length - 1];
        } else {
          timeOutRef.current = setTimeout(() => {
            handleNextMessage(game);
            setGameCacheState({ type: "SET_GAME_CACHE_STATE", payload: { ...game } });
            timeOutRef.current = null;
          }, CURTAIN_ANIMATION_DELAY);
        }
        return;
      }

      // If the previous game state was qualification or in round, we need to close the curtains
      if ([GameStates.QUALIFICATION, GameStates.IN_ROUND].includes(gameCacheState.state) && game?.state === GameStates.RANKING) {
        setCurtainsOpen(false);
        timeOutRef.current = setTimeout(() => {
          setGameCacheState({ type: "SET_GAME_CACHE_STATE", payload: { ...game } });
          setCurtainsOpen(true);
          timeOutRef.current = null;
        }, CURTAIN_DELAY_BY_STATE[GameStates.QUALIFICATION]);
        return;
      }

      // If the next state is qualification, just close the curtains
      if (game?.state === GameStates.QUALIFICATION) {
        setCurtainsOpen(false);
        // set the data after the animation
        setTimeout(() => {
          setGameCacheState({ type: "SET_GAME_CACHE_STATE", payload: { ...game } });
        }, CURTAIN_ANIMATION_DELAY);

        // open the curtains after the animation and the game state delay
        timeOutRef.current = setTimeout(() => {
          setCurtainsOpen(true);
          timeOutRef.current = null;
        }, CURTAIN_DELAY_BY_STATE[GameStates.QUALIFICATION] + CURTAIN_ANIMATION_DELAY);
        return;
      }

      if (game?.state === GameStates.FINAL_RANKING) {
        setCurtainsOpen(false);
        timeOutRef.current = setTimeout(() => {
          setGameCacheState({ type: "SET_GAME_CACHE_STATE", payload: { ...game } });
          setCurtainsOpen(true);
          timeOutRef.current = null;
        }, CURTAIN_DELAY_BY_STATE[GameStates.FINAL_RANKING]);
        return;
      }

      timeOutRef.current = setTimeout(() => {
        setGameCacheState({ type: "SET_GAME_CACHE_STATE", payload: { ...game } });
        setCurtainsOpen(true);
        timeOutRef.current = null;
      }, CURTAIN_DELAY_BY_STATE[game?.state] || 0);
      return;
    }

    if (!timeOutRef.current) {
      // Update game cache state ignoring the state property, this is used when we want to update the game cache state but we don't want to reset the curtain animation.
      setGameCacheState({ type: "SET_GAME_CACHE_STATE_NON_CRITICAL_PROPS", payload: { ...game } });
    }

  }

  function clearAllSpinnerTimeOuts() {
    for (let i = 0; i < spinnerTimers.current.length; i++) {
      clearTimeout(spinnerTimers.current[i]);
    }
    timeOutRef.current = null;
  }

  //recibimnos un clip de animacion que mostrar
  //lo mostramos por un tiempo y despues pasamos al idle de regreso
  function PlayAnimation(clip, customTime = null) {
    if (clip != null) {
      setAnimation(clip);
    } else {
      setAnimation(ANIMATIONS.talking);
    }

    let t = 2500;
    if (clip === ANIMATIONS.hello) { t = 4000; }
    if (clip === ANIMATIONS.celebrating) { t = 3000; }

    if (customTime) {
      t = customTime;
    }

    setTimeout(() => {
      setAnimation(ANIMATIONS.idle);
    }, t);
  }

  if (!game) {
    return (
      <div id="clouds">
        <div className="cloud x1"></div>
        <div className="cloud x2"></div>
        <div className="cloud x3"></div>
        <div className="cloud x4"></div>
        <div className="cloud x5"></div>
      </div>
    )
  }

  return (
    <GameProvider user={user} game={gameCacheState} socket={socket} room={room}>
      <div id="clouds">
        <div className="cloud x1"></div>
        <div className="cloud x2"></div>
        <div className="cloud x3"></div>
        <div className="cloud x4"></div>
        <div className="cloud x5"></div>
      </div>
      <div className={styles.screen}>

        {gameCacheState.state !== '' &&
          <div className={styles.curtain}>
            <div className={styles.zigZagLineV}> </div>
          </div>
        }


        <div className={styles.gameInProgress}>
          <Modal show={!!inGameMessage}>
            <div className="absolute w-full h-full z-150 top-0 flex flex-col items-center justify-center">
              <QuestionTextContainer text={inGameMessage} animated />
            </div>
          </Modal>
          {gameCacheState.state === GameStates.JOKER && <SpinnerComponent playAnimation={PlayAnimation} />}
          {gameCacheState.state === GameStates.IN_ROUND && <QuestionComponent playAnimation={PlayAnimation} />}
          {gameCacheState.state === GameStates.QUALIFICATION && <QualificationComponent playAnimation={PlayAnimation} />}
          {(gameCacheState.state === "final_ranking" || gameCacheState.state === "ranking" || gameCacheState.state === "winner") && <RankingComponent playAnimation={PlayAnimation} />}
        </div>
      </div>
      <Modal show>
        <div className="absolute w-full h-full z-100 top-0 pointer-events-none">
          <LoadingDoorComponent open={curtainsOpen} />
        </div>
      </Modal>
    </GameProvider>
  );
}

export default GameInProgress;